<div class="h-fit bg-background flex flex-col gap-4">
    <div class="flex flex-row panel-content items-start py-8 px-4 bg-on-primary rounded-xl">
        <fa-icon
            class="text-secondary mr-4 cursor-pointer mt-1 shrink-0"
            size="2x"
            [icon]="['far', 'arrow-left']"
            [matTooltip]="'back' | transloco"
            (click)="back()" />

        <div
            class="flex flex-row w-full gap-1.5"
            [class.items-end]="editEntityDetailService.isEditMode()">
            <div
                class="flex flex-col min-w-0 gap-5"
                [class.flex-1]="editEntityDetailService.isEditMode()">
                @if (isEditModeActiveAndBusinessIdentifierEditable()) {
                    <app-entity-attributes-input
                        [attribute]="businessIdentifierAttribute()!"
                        [attributesForm]="
                            !!businessBookmarkedAttributesForm()
                                ? editEntityDetailService.bookmarkedAttributesForm()!
                                : editEntityDetailService.notBookmarkedAttributesForm()!
                        "
                        [columnNamesWithReference]="columnNamesWithReference()"
                        [disabled]="
                            !isLayerEditable() || nonEditableColumnsName().includes(businessIdentifierAttribute()!.key)
                        "
                        [informationSchemaTable]="
                            editEntityDetailService.findCurrentInformationSchemaTable(
                                editEntityDetailService.businessIdentifierKey()!
                            )
                        "
                        [label]="businessIdentifierLabel()"
                        [labelTooltip]="businessIdentifierLabelTooltip()"
                        [tooltip]="nonEditableColumnsReasonByName()[businessIdentifierAttribute()!.key] ?? undefined" />
                } @else {
                    <span class="font-medium text-2xl break-words">
                        {{ identifier() }}
                    </span>
                }

                <ng-content />
            </div>

            <div class="flex flex-col gap-3.5 flex-1">
                @if (geomBboxEntity()) {
                    <app-button
                        customClass="h-10"
                        iconSize="xl"
                        [icon]="['fad', 'arrows-to-eye']"
                        [tooltip]="'centerOnSelectedEntity' | transloco"
                        [type]="ButtonType.SECONDARY"
                        (onClick)="centerMapOnEntityAndZoomIn.emit()" />
                }

                @if (editEntityDetailService.isEditMode()) {
                    <app-button
                        [disabled]="isGeometryEditButtonDisabled()"
                        [icon]="['fas', 'pen']"
                        [label]="
                            (isEntityGeomEditActive() ? 'editMode.stopGeomEdit' : 'editMode.editGeometry') | transloco
                        "
                        [matTooltip]="geometryEditButtonTooltip() | transloco"
                        [type]="ButtonType.SECONDARY"
                        (onClick)="onGeometryEditButtonClick()" />
                }
            </div>
        </div>
    </div>

    @if (availableInteractionCount()) {
        <div class="p-5 bg-on-primary rounded-xl">
            <app-expansion-panel [isExpanded]="true">
                <span
                    class="font-medium text-xl"
                    header
                    >{{ 'interaction' | transloco: { COUNT: availableInteractionCount() } }}</span
                >
                <div
                    class="flex flex-col"
                    content>
                    @if (hasBoxOrBayPlan()) {
                        <app-synoptic-action-item
                            [disabled]="!canReadSynoptic()"
                            [icon]="['far', 'window-restore']"
                            [label]="'openBoxOrBayPlan' | transloco"
                            [matTooltip]="!canReadSynoptic() ? ('insufficientPermissions' | transloco) : ''"
                            (action)="openBoxOrBayPlan.emit()" />
                    }

                    @if (hasSynoptic()) {
                        <app-synoptic-action-item
                            [disabled]="!canReadSynoptic()"
                            [icon]="['far', 'project-diagram']"
                            [label]="'openSynopticPage' | transloco"
                            [matTooltip]="!canReadSynoptic() ? ('insufficientPermissions' | transloco) : ''"
                            (action)="goToSynopticPanel.emit()" />
                    }

                    @if (hasOpticalPath()) {
                        <app-synoptic-action-item
                            [icon]="['fas', 'arrow-down-to-line']"
                            [label]="'exportOpticalRoute' | transloco"
                            (action)="exportOpticalPath.emit()" />

                        @if (networkPathService.canAccessNetworkPath()) {
                            <app-synoptic-action-item
                                [icon]="['fal', 'route']"
                                [label]="'viewOpticalPath' | transloco"
                                (action)="viewOpticalPath.emit()" />
                        }
                    }

                    @if (isEntityUsedInBreak() && !editEntityDetailService.isEditMode()) {
                        <app-synoptic-action-item
                            [icon]="['fas', 'location-crosshairs']"
                            [label]="'locateBreaks' | transloco"
                            (action)="openBreakModal.emit()" />
                    }
                </div>
            </app-expansion-panel>
        </div>
    }

    @if (canReadDocuments() && !editEntityDetailService.isEditMode()) {
        <div class="p-5 bg-on-primary rounded-xl">
            <app-entity-link-document-list
                [data]="documentData()"
                [dataEntity]="dataEntity()" />
        </div>
    }

    @if (editEntityDetailService.isEditMode() && !!editEntityDetailService.recomputeError()) {
        <div class="p-5 bg-on-primary rounded-xl border-solid border-2 border-error">
            <app-expansion-panel [isExpanded]="true">
                <span
                    class="font-medium text-xl"
                    header
                    ><fa-icon
                        [class]="'text-error'"
                        [icon]="['fas', 'triangle-exclamation']" />
                    {{ 'error' | transloco: { COUNT: 1 } }}</span
                >
                <div
                    class="flex flex-col px-2 text-base"
                    content>
                    {{ editEntityDetailService.recomputeError() }}
                </div>
            </app-expansion-panel>
        </div>
    }

    <div class="p-5 bg-on-primary rounded-xl">
        <app-entity-attributes
            [attributesInformations]="attributesInformation()"
            [bookmarkedAttributes]="bookmarkedAttributes()"
            [columnNamesWithReference]="columnNamesWithReference()"
            [columnsByName]="columnsByName()"
            [isLayerEditable]="isLayerEditable()"
            [nonEditableColumnsName]="nonEditableColumnsName()"
            [nonEditableColumnsReasonByName]="nonEditableColumnsReasonByName()"
            [notBookmarkedAttributes]="notBookmarkedAttributes()" />
    </div>

    @if (isLayerRelated()) {
        <div class="p-5 bg-on-primary rounded-xl">
            <app-related-layer-list
                [layerRelations]="layerRelations()"
                [sourceEntityId]="entityId()!"
                [sourceGeometry]="currentGeometry()"
                [sourceLayerId]="editEntityDetailService.layer()!.id"
                [sourceTableName]="editEntityDetailService.layer()!.tableName!" />
        </div>
    }

    @if (editEntityDetailService.isEditMode()) {
        <div class="flex gap-2 rounded-md bg-white px-2 z-10 py-3 flex-col bottom-0 sticky">
            <div class="flex justify-between gap-2">
                <app-button
                    [disabled]="isDeleteButtonDisabled() || !isLayerEditable()"
                    [icon]="['fas', 'trash']"
                    [iconSize]="'sm'"
                    [label]="'delete' | transloco"
                    [matTooltip]="
                        !mapEditModeService.editPermissions().canDelete
                            ? ('editMode.noPermissionToDelete' | transloco)
                            : ''
                    "
                    [type]="ButtonType.ALERT_SECONDARY"
                    (onClick)="delete()" />

                <div class="flex flex-row gap-2">
                    @if (isFromStandardizeDeposit()) {
                        <app-button
                            [disabled]="!isLayerEditable()"
                            [label]="'recompute' | transloco"
                            [type]="ButtonType.SECONDARY"
                            (onClick)="recomputeEntityProperties()" />
                    }

                    <app-button
                        [disabled]="editEntityDetailService.areFormsOnError() || !isLayerEditable()"
                        [label]="'validate' | transloco"
                        [type]="ButtonType.PRIMARY"
                        (onClick)="validateEdition()" />
                </div>
            </div>

            @if (!isLayerEditable()) {
                <span class="flex self-center text-sm font-medium text-gray-500">
                    {{ 'editMode.layerStandardNotEditable' | transloco }}
                </span>
            }
        </div>
    }
</div>
